import { Link } from '@tanstack/react-router';
import { useState } from 'react';

import type { EnvKey } from '@/app/site/scope';
import catalog from '@/features/catalog/Catalog.module.scss';
import type { FieldBody, TypeBody } from '@/shared/api/generated/model';
import { apiURL } from '@/shared/config';
import { Badge } from '@/shared/ui/Badge';
import { CopyButton } from '@/shared/ui/CopyButton';
import { Segmented } from '@/shared/ui/Segmented';

import styles from './FormsPage.module.scss';
import { sampleBody, submissionFields } from './submissions';

type Sample = 'server' | 'browser' | 'curl';

/**
 * Как подключить форму на сайте: адрес, поля и готовый код — его можно
 * отдать разработчику сайта как есть. Ключ — в «Настройки → Подключение
 * сайта».
 */
export function FormApi({
  siteId,
  env,
  type,
  fields,
}: {
  siteId: string;
  env: EnvKey;
  type: TypeBody;
  fields: FieldBody[];
}) {
  const [sample, setSample] = useState<Sample>('server');
  const endpoint = `${apiURL()}/v1/collections/${type.slug}`;
  const body = JSON.stringify(sampleBody(fields), null, 2);
  const open = type.api_access.create === 'public';

  const code: Record<Sample, string> = {
    server: [
      '// Маршрут сервера сайта (Nuxt: server/api/request.post.ts) — ключ не уходит в браузер,',
      '// а Faceta считает предел частоты по адресу посетителя.',
      "import { facetaFromEnv } from '@pet-p-team/faceta-sdk';",
      '',
      'const faceta = facetaFromEnv(process.env);',
      '',
      'export default defineEventHandler(async (event) => {',
      '  const { website, ...data } = await readBody(event);',
      '',
      `  await faceta.create('${type.slug}', data, {`,
      '    trap: website, // скрытое поле-ловушка формы',
      '    visitorIP: getRequestIP(event, { xForwardedFor: true }),',
      '  });',
      '',
      '  return { ok: true };',
      '});',
    ].join('\n'),
    browser: [
      '// Прямо со страницы — публичным ключом (FACETA_PUBLIC_KEY), с адреса сайта из настроек.',
      "import { createFaceta } from '@pet-p-team/faceta-sdk';",
      '',
      "const faceta = createFaceta({ apiUrl: '" + apiURL() + "', key: FACETA_PUBLIC_KEY });",
      '',
      `await faceta.create('${type.slug}', ${body}, {`,
      '  trap: form.website.value, // скрытое поле-ловушка',
      '});',
    ].join('\n'),
    curl: [
      `curl -X POST '${endpoint}' \\`,
      "  -H 'Authorization: Bearer $FACETA_KEY' \\",
      "  -H 'Content-Type: application/json' \\",
      `  -d '${JSON.stringify({ data: sampleBody(fields) })}'`,
    ].join('\n'),
  };

  return (
    <div className={styles['api']}>
      <section className={styles['api__block']}>
        <h2 className={styles['api__title']}>
          Адрес
          {open ? (
            <Badge size="sm" tone="success">
              принимает заявки
            </Badge>
          ) : (
            <Badge size="sm" tone="warning">
              на паузе — включите «Создавать: всем» в настройках
            </Badge>
          )}
        </h2>
        <div className={styles['endpoint']}>
          <span className={styles['method']}>POST</span>
          <code>{endpoint}</code>
          <CopyButton value={endpoint} />
        </div>
        <p className={styles['muted']}>
          Ключ сайта — в{' '}
          <Link
            to="/sites/$siteId/$env/settings"
            params={{ siteId, env }}
            search={{ tab: 'connect' }}
          >
            «Настройки → Подключение сайта»
          </Link>
          . Заявка приходит сюда новой; ответ — <code>{'{ "id": "…" }'}</code>, ошибки полей — 422
          со словами.
        </p>
      </section>

      <section className={styles['api__block']}>
        <h2 className={styles['api__title']}>Поля</h2>
        <div className={catalog['table']}>
          <table>
            <thead>
              <tr>
                <th>Ключ</th>
                <th>Название</th>
                <th>Тип</th>
                <th>Обязательное</th>
              </tr>
            </thead>
            <tbody>
              {submissionFields(fields).map((field) => (
                <tr key={field.key}>
                  <td className={catalog['mono']}>{field.key}</td>
                  <td>
                    {field.name}
                    {field.options['private'] === true && (
                      <span className={styles['muted']}>
                        {' '}
                        · только в админке, с сайта не задаётся
                      </span>
                    )}
                  </td>
                  <td>{field.type_title}</td>
                  <td>{field.required ? 'да' : '—'}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </section>

      <section className={styles['api__block']}>
        <h2 className={styles['api__title']}>Код для сайта</h2>
        <Segmented
          value={sample}
          onValueChange={(next) => {
            setSample(next as Sample);
          }}
          aria-label="Пример кода"
          options={[
            { value: 'server', label: 'С сервера сайта' },
            { value: 'browser', label: 'Из браузера' },
            { value: 'curl', label: 'curl' },
          ]}
        />
        <div className={styles['code']}>
          <pre>{code[sample]}</pre>
          <CopyButton value={code[sample]} className={styles['code__copy'] ?? ''} />
        </div>
      </section>

      <section className={styles['api__block']}>
        <h2 className={styles['api__title']}>Защита от спама</h2>
        <ul className={styles['notes']}>
          <li>
            <b>Ловушка.</b> Добавьте в форму скрытое поле (например, <code>website</code>) и
            передайте его как <code>trap</code>. Человек его не видит, бот заполнит — заявка не
            сохранится, а ответ будет как при успехе.
          </li>
          <li>
            <b>Частота.</b> С одного адреса — не больше 5 заявок в минуту и 30 в час, дальше 429.
            Шлёте с сервера сайта — передайте адрес посетителя в <code>visitorIP</code>, иначе
            предел общий на всех.
          </li>
          <li>
            <b>Размер.</b> Тело заявки — до 64 КБ.
          </li>
        </ul>
      </section>
    </div>
  );
}
